// toolkit.ts — the "Your toolkits" exercises: gentle, practical tools the user
// returns to in tough moments. Ported from content/Exercise builder. Each tool is
// a short stepper (do / journal / media / action / safety blocks). Journal prompts
// render as read-only cards that point the user to their own journal (in-app
// journaling is off). On-screen copy uses no em-dashes; icons are the app's own
// SVG set (never emoji).
//
// UNLOCKING (content/Exercise builder/trigger-lessons.js):
//   • The Boundary & urge tools (ALWAYS_OPEN) are open from the start.
//   • Every other tool unlocks once its teaching lesson is completed
//     (unlockLessonId — a real programme step id).
//
// SINGLE SOURCE OF TRUTH: the tool CONTENT (text/steps/blurbs) lives in
// toolkit.content.json — gate-neutral and presentation-neutral — which is also served
// at GET /api/toolkit and consumed verbatim by the GOGL podcast app (it gates on its own
// audio progress). Here we rebuild the typed TOOLS array by overlaying ReNurture's
// unlock gates (toolkitGates.ts) onto that shared content. toolkit.snapshot.test.ts
// proves this produces a byte-identical TOOLS array (no content/behaviour change).
import type { IconName } from '@/components/icons';
import contentJson from './toolkit.content.json';
import { TOOLKIT_GATES } from './toolkitGates';

export type ToolTheme = 'green' | 'lav' | 'amber' | 'rose';

export interface ToolStep {
  kind: 'do' | 'media' | 'journal';
  kicker?: string;
  title: string;
  body?: string[];
  list?: string[];
  /** Render the list with open-circle markers (a gentle checklist look). */
  check?: boolean;
  /** "In your journal" prompt cards (read-only — the user writes in their own journal). */
  journal?: string[];
  journalLabel?: string;
  jhint?: string;
  /** "What to do" action cards. */
  action?: string[];
  actionLabel?: string;
  ahint?: string;
  /** Safety-first cards (rendered in a red, urgent treatment). */
  safety?: string[];
  safetyLabel?: string;
  shint?: string;
  /** A guided-audio placeholder (media label). */
  media?: string;
  /** A collapsible bank of worked examples, grouped by situation. Tapping a group
   *  reveals its suggested wordings. `tone` (soft/neutral/firm) and `why` (one-line
   *  "why this works") are optional. Used by the boundary tool's write + consequence
   *  stages. Rendered as an accordion in ExerciseViewer. */
  examples?: {
    label?: string;
    groups: { title: string; items: { text: string; tone?: 'soft' | 'neutral' | 'firm'; why?: string }[] }[];
  };
}

export interface Tool {
  id: string;
  group: string;
  theme: ToolTheme;
  time: string;
  icon: IconName;
  title: string;
  blurb: string;
  steps: ToolStep[];
  /** Real programme step id that unlocks this tool; undefined = always open. */
  unlockLessonId?: string;
  /** Human label for the unlocking lesson (shown on the locked tile). */
  unlockLesson?: string;
  unlockCourse?: string;
  /** When set, this is a LINK tool: opening it opens this URL in the browser
   *  (no stepper). `steps` stays empty for link tools. */
  linkUrl?: string;
  /** Button/affordance label for a link tool (defaults to "Open"). */
  linkLabel?: string;
  /** Optional label for the intro "Begin" button (e.g. "Begin building your plan").
   *  Defaults to "Begin". */
  beginLabel?: string;
}

/** The shared content tool: a Tool with the ReNurture-specific gate fields removed.
 *  This is exactly the shape carried in toolkit.content.json. */
export type ToolContent = Omit<Tool, 'unlockLessonId' | 'unlockLesson' | 'unlockCourse'>;

interface ToolkitContentFile {
  schemaVersion: number;
  groupOrder: string[];
  tools: ToolContent[];
}

// JSON-inferred types widen the unions (theme: string, icon: string, kind: string), so
// assert through `unknown` to the authored shape. The snapshot test is the real guard.
const CONTENT = contentJson as unknown as ToolkitContentFile;

/** Group display order. Compassion practices leads (always open, already live on the
 *  website); the always-open safety tools follow. */
export const TOOL_GROUP_ORDER = [
  'Compassion practices',
  'Boundary & urge tools',
  'Key skills · in the moment',
  'Soothing your old friends',
  'Building self-worth',
  'Maintenance plan',
];

/** Per-theme accent colours, drawn to sit beside the app's amber/green palette.
 *  `soft`/`border` are light tints for readable headers (dark text on a pale wash);
 *  `bg` (the saturated colour) is kept for small accents like the done medallion. */
export const TOOL_THEME: Record<ToolTheme, { bg: string; soft: string; border: string; fg: string }> = {
  green: { bg: '#1f7d5e', soft: 'rgba(29,158,117,0.12)', border: 'rgba(29,158,117,0.24)', fg: 'var(--green-deep)' },
  amber: { bg: '#b07d1e', soft: 'rgba(200,134,26,0.12)', border: 'rgba(200,134,26,0.26)', fg: 'var(--amber-deep)' },
  lav: { bg: '#6a5cb0', soft: 'rgba(106,92,176,0.13)', border: 'rgba(106,92,176,0.26)', fg: '#5a4ea0' },
  rose: { bg: '#9c5b66', soft: 'rgba(156,91,102,0.13)', border: 'rgba(156,91,102,0.26)', fg: '#9c4d52' },
};

/** Maps each abstract tool theme to a Library category key, so the toolkit shares
 *  one colour family with the Library (src/services/library.ts → CATEGORY_TOKENS):
 *  lav→Hold (Boundaries), amber→Rebuild, green→Respond. `rose` has no Library
 *  equivalent (the Library palette has no red/rose), so it keeps its own TOOL_THEME
 *  colours above. Consumers fall back to TOOL_THEME when the key is null. */
export const THEME_LIBRARY_KEY: Record<ToolTheme, string | null> = {
  green: 'Respond',
  amber: 'Rebuild',
  lav: 'Hold',
  rose: null,
};

/** The toolkits — rebuilt from the shared content + ReNurture's gate overlay. */
export const TOOLS: Tool[] = CONTENT.tools.map((t) => {
  const gate = TOOLKIT_GATES[t.id];
  return gate ? { ...t, ...gate } : (t as Tool);
});

/** Tools that are open from the start (no completion required). NOTE: kept as an
 *  explicit list — `dice` has no gate yet is deliberately NOT always-open, so this
 *  cannot be derived from "tools without unlockLessonId". `calm` is intentionally
 *  always-open: a grounding tool a survivor may need in a crisis must never be locked. */
export const ALWAYS_OPEN = ['beaturge', 'boundary', 'communicate', 'escalation', 'calm'];

/** True when a tool is unlocked, given the set of completed lesson/step ids. */
export function isToolUnlocked(tool: Tool, completed: ReadonlySet<string> | string[]): boolean {
  if (!tool.unlockLessonId) return true;
  const has = Array.isArray(completed) ? completed.includes(tool.unlockLessonId) : completed.has(tool.unlockLessonId);
  return has;
}

/** All tools whose gate is now satisfied (excludes the always-open ones). */
export function newlyUnlockableGatedTools(completed: string[]): Tool[] {
  const set = new Set(completed);
  return TOOLS.filter((t) => t.unlockLessonId && set.has(t.unlockLessonId));
}

export function toolById(id: string): Tool | undefined {
  return TOOLS.find((t) => t.id === id);
}
